import Head from 'next/head'
import { stringify } from 'query-string'
import React, { useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import clsx from 'clsx'

import { Icon } from '@wartek-id/icon'
import { Text } from '@wartek-id/text'

import { determineTopicState, shouldHideNav } from '../helper'
import useTopicDetails from 'app/PelatihanMandiriPage/hooks/useTopicDetails'
import AksiNyataCard from '../partials/AksiNyata/AksiNyataCard'
import ModuleCard from '../partials/Module/ModuleCard'
import ModulePlaceholder from '../partials/Module/ModulePlaceholder'
import ProgressionBar from '../partials/Topic/ProgressionBar'
import TopicHeader from '../partials/Topic/TopicHeader'
import GenericList from '../components/GenericList'
import { MILKvKeys, useGetKeyPairValue } from 'api/microlearning/useAPIKeyValue'
import { EmptyState } from 'components/EmptyState'
import WebinarCard from 'app/PelatihanMandiriPage/partials/Webinar/WebinarCard'
import CertificateStatusCard from '../partials/Certificate/CertificateStatusCard'
import RKMMBanner from '../partials/Topic/RKMMBanner'
import {
  filterByDate,
  filterByModuleTopicId,
  sortByDate,
} from './TopicDetails.helper.WebinarEntries'
import { useSETrackerEvent } from '../hooks/useSETrackEvent'
import CertificateTopic from '../components/CertificateTopic'
import TopicInfo from '../partials/Topic/TopicInfo'

import type { WebinarEntry } from 'api/microlearning/types'
import type { TopicProgressState } from '../helper'

const TopicDetails = ({
  onPageLoaded,
  isDesktop = false,
}: {
  onPageLoaded?: (topicName?: string) => void
  isDesktop?: boolean
}) => {
  const { query, back, replace } = useRouter()
  const { topik, filter } = query
  const { from = 'topik', menuId = topik } = query
  const {
    moduleWithProgress,
    topicData,
    fetchStatus,
    progressionData,
    revisionItems,
    certifiable,
    appeal,
    refetchProgress,
  } = useTopicDetails(topik as string)

  const isTopicUpdated = topicData?.isTopicUpdated

  const [isAksiNyataLocked, setIsAksiNyataLocked] = useState(true)
  const isDaljabTopic = topicData?.topicType === 'DALJAB'
  const webinarKv = useGetKeyPairValue(
    MILKvKeys.WebinarEntries,
    !!fetchStatus,
    {
      select: (data) => {
        return {
          data: sortByDate(
            filterByDate(
              filterByModuleTopicId(
                data.data.webinar as WebinarEntry[],
                parseInt(`${topik}`, 10)
              )
            )
          ).shift(),
          meta: {},
        }
      },
    }
  )

  const { SETrackTopicList } = useSETrackerEvent()

  const handleTracker = (id: string | number) => {
    if (filter) {
      SETrackTopicList({
        action_type: 'choose_module',
        action_filter: filter,
        topic_id: id,
      })
    }
  }

  // Determine if aksinyata is locked or not
  useEffect(() => {
    if (fetchStatus === 'success') {
      const topicState = determineTopicState(progressionData)
      const optNotLocked: TopicProgressState[] = [
        'MODULE_DONE',
        'AKSI_NYATA_CREATED',
        'AKSI_NYATA_ON_PROGRESS',
        'AKSI_NYATA_DONE',
        'AKSI_NYATA_NEED_REVISION',
        'AKSI_NYATA_VALIDATED',
        'AKSI_NYATA_APPEAL',
      ]
      if (optNotLocked.includes(topicState)) {
        setIsAksiNyataLocked(false)
      }

      // redirect if topicType `DALJAB`
      if (isDaljabTopic) {
        replace('/pelatihan-mandiri/404')
      }
    }
    onPageLoaded?.(topicData?.name)
  }, [
    progressionData,
    fetchStatus,
    onPageLoaded,
    topicData?.name,
    isDaljabTopic,
    replace,
  ])

  const TopicContent = () => (
    <>
      <div className="relative">
        <TopicInfo
          isTopicUpdated={isTopicUpdated}
          pointers={topicData?.values}
          shouldShowTab={Boolean(topicData?.contextualizedPageId)}
        />
        <GenericList
          className="relative px-3"
          beforeList={
            <>
              {webinarKv.isSuccess && !!webinarKv.data?.data && (
                <div className="mt-6">
                  <div className="mb-2 flex justify-between">
                    <Text variant="heading-md">Webinar</Text>
                    <Link
                      href={`/${
                        process.env.NEXT_PUBLIC_TEMP_PELATIHAN_MANDIRI_PATH ||
                        'pelatihan-mandiri'
                      }/webinar`}
                      passHref
                    >
                      <Text as="a" variant="body-sm">
                        <strong className="text-blue-70">
                          Lihat Jadwal Lain
                        </strong>
                      </Text>
                    </Link>
                  </div>
                  <div
                    className={clsx(
                      'mb-4 last:mb-0',
                      isDesktop && 'grid grid-cols-2 gap-2'
                    )}
                  >
                    <WebinarCard
                      id={webinarKv.data.data.id}
                      href={`/${
                        process.env.NEXT_PUBLIC_TEMP_PELATIHAN_MANDIRI_PATH ||
                        'pelatihan-mandiri'
                      }/webinar/${webinarKv.data?.data?.id}`}
                      topicName={webinarKv.data.data.topic?.title}
                      topicId={
                        webinarKv.data.data.topic?.id as unknown as string
                      }
                      title={webinarKv.data.data.title}
                      speakers={webinarKv.data.data.speakers}
                      startDate={webinarKv.data.data.startDate}
                      endDate={webinarKv.data.data.endDate}
                    />
                  </div>
                </div>
              )}
              <div className="mb-2 mt-6">
                <Text variant="heading-md">Modul Pelatihan</Text>
                <Text variant="body" color="subdued">
                  Silakan pelajari modul sesuai urutan
                </Text>
              </div>
            </>
          }
          data={moduleWithProgress}
          dataState={isDaljabTopic ? 'loading' : fetchStatus}
          loadingComponent={
            <div className="pt-4">
              <ModulePlaceholder amount={3} />
            </div>
          }
          emptyComponent={
            <EmptyState
              title="Konten belum tersedia"
              description="Belum ada topik yang dapat dipelajari saat ini, silahkan coba lagi nanti."
            />
          }
          renderItem={(
            { id, name, duration, totalMaterial, progress, badgeText },
            index
          ) => (
            <div onClick={() => handleTracker(id)}>
              <ModuleCard
                id={id}
                number={index + 1}
                name={name}
                duration={duration}
                totalMaterial={totalMaterial}
                isLocked={!progress?.prerequisiteCompleted}
                isCompleted={progress?.postTest === 'passed'}
                badgeText={badgeText}
                href={
                  `/${
                    process.env.NEXT_PUBLIC_TEMP_PELATIHAN_MANDIRI_PATH ||
                    'pelatihan-mandiri'
                  }/modul/${id}?` +
                  stringify({
                    topik: query.topik,
                    topik_name: topicData?.name,
                    from,
                    menuId,
                  })
                }
              />
            </div>
          )}
          listKey={({ id }) => id}
          itemWrapperClassName={isDesktop && 'grid grid-cols-2 gap-2'}
        />
      </div>
    </>
  )

  if (isDesktop) {
    return (
      <>
        <Head>
          <title>Pelatihan Mandiri | Topik {topicData?.name}</title>
        </Head>
        <div className="flex flex-row">
          {query.from && (
            <Icon as="i" color="default" className="mr-2" onClick={back}>
              arrow_back
            </Icon>
          )}
          <div>
            <Text className={clsx('pb-1')}>TOPIK</Text>
            <Text className={clsx('pb-3')} variant="heading-lg">
              {topicData?.name}
            </Text>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-2">
          <TopicHeader
            title={topicData?.name}
            shouldShowTab={Boolean(topicData?.contextualizedPageId)}
            shouldShowInfo={false}
          />

          <div className="hidden">
            <div className="sticky top-[54px] z-10 px-8 py-3 h-24 -mx-3 bg-gray-5">
              <ProgressionBar progressData={progressionData} />
            </div>
            <div className="relative mb-4 -mx-3">
              <hr className="border-subdued" />
            </div>
          </div>
          <TopicContent />
          {fetchStatus === 'success' && (
            <div className="relative px-3 z-0">
              <AksiNyataCard
                topikId={query.topik as string}
                name={topicData?.name}
                selectedOptionTitle={
                  progressionData?.aksiNyata?.selectedOption?.title
                }
                optionsAmount={topicData?.aksiNyataOptionCount}
                aksiNyataSessionState={progressionData?.aksiNyata?.state}
                isLocked={isAksiNyataLocked}
                aksiNyataId={progressionData?.aksiNyata?.id}
              />
            </div>
          )}
          {fetchStatus === 'success' && (
            <CertificateStatusCard
              state={progressionData?.aksiNyata?.state}
              revisionItems={revisionItems || []}
              topicName={topicData?.name}
              certifiable={certifiable}
              appeal={appeal}
              refetchProgress={refetchProgress}
            />
          )}
          {!isDesktop && <RKMMBanner />}
        </div>
      </>
    )
  }

  return (
    <>
      <Head>
        <title>Pelatihan Mandiri | Topik {topicData?.name}</title>
      </Head>
      <div
        className="relative pb-6 bg-gray-10 z-0"
        style={{
          minHeight: shouldHideNav() ? '100vh' : 'calc(100vh - 3.5rem)',
        }}
      >
        {!shouldHideNav() && (
          <div className="sticky top-[54px] z-30">
            <TopicHeader
              title={topicData?.name}
              shouldShowTab={Boolean(topicData?.contextualizedPageId)}
            />

            <div className="hidden">
              <div className="sticky top-[54px] z-10 px-8 py-3 h-24 -mx-3 bg-gray-5">
                <ProgressionBar progressData={progressionData} />
              </div>
              <div className="relative mb-4 -mx-3">
                <hr className="border-subdued" />
              </div>
            </div>
          </div>
        )}
        <TopicContent />
        <CertificateTopic progressData={progressionData} />
        {fetchStatus === 'success' && (
          <div className="relative px-3 z-0">
            <AksiNyataCard
              topikId={query.topik as string}
              name={topicData?.name}
              selectedOptionTitle={
                progressionData?.aksiNyata?.selectedOption?.title
              }
              optionsAmount={topicData?.aksiNyataOptionCount}
              aksiNyataSessionState={progressionData?.aksiNyata?.state}
              isLocked={isAksiNyataLocked}
              aksiNyataId={progressionData?.aksiNyata?.id}
            />
          </div>
        )}
        {fetchStatus === 'success' && (
          <CertificateStatusCard
            state={progressionData?.aksiNyata?.state}
            revisionItems={revisionItems || []}
            topicName={topicData?.name}
            certifiable={certifiable}
            appeal={appeal}
            refetchProgress={refetchProgress}
          />
        )}
        <RKMMBanner />
      </div>
    </>
  )
}

export default TopicDetails
